import {
  Ambulance, BedDouble, BookOpen, BrainCircuit, Building2, CalendarClock, ClipboardList, Clock, Cloud,
  Activity, CodeXml, CreditCard, Database, FlaskConical, GitMerge, LayoutGrid, Library, ListChecks, ListOrdered, Lock, MapPin,
  MessageSquare, Palette, PenLine, Pill, Receipt, ReceiptIndianRupee, ScrollText, Settings, ShieldCheck, SlidersHorizontal, Sparkles,
  Stethoscope, Tags, Users, Warehouse, type LucideIcon,
} from 'lucide-react';

/**
 * The Admin Panel's information architecture — every section and setting,
 * in rail order. Pure data (no setting components are imported here), so the
 * command palette and the topbar breadcrumb can read it without pulling the
 * 26 setting screens into their bundles.
 *
 * `id` is the setting's URL slug (/admin/<section>/<id>) and its key in
 * AdminModule's component map. `module` is the grant that shows it; grants are
 * unchanged by the 2026-10-20 regrouping — only labels and sections moved
 * (docs/design/admin-panel/Admin_Panel_Wireframe.html, section B). Renaming a
 * grant would need a migration and would revoke the setting for every role
 * that already holds it.
 *
 * `description` is searched alongside the label (typing "GST", "password" or
 * "consent" finds a setting whose title lacks the word) and heads the page.
 */
export const ADMIN_SECTIONS = [
  { id: 'people', label: 'People & Access', icon: Users,
    description: 'Who can sign in, what they can open, and duplicate patient records.' },
  { id: 'clinical', label: 'Clinical', icon: Stethoscope,
    description: 'The forms, templates and rules clinicians work inside.' },
  { id: 'facility', label: 'Facility', icon: Building2,
    description: 'Sites, wards and beds, and when doctors consult.' },
  { id: 'pharmacy-lab', label: 'Pharmacy & Lab', icon: Pill,
    description: 'The medicine catalogue, stocking and dispensing policy, and laboratory test masters.' },
  { id: 'billing', label: 'Billing & Insurance', icon: CreditCard,
    description: 'Tax, invoice numbering, and the insurers and schemes claims go to.' },
  { id: 'communication', label: 'Communication', icon: MessageSquare,
    description: 'The SMS and WhatsApp messages the hospital sends patients.' },
  { id: 'ai', label: 'AI', icon: Sparkles,
    description: 'Whether AI runs, what it may do, and a record of every call it made.' },
  { id: 'system', label: 'System & Compliance', icon: Settings,
    description: 'Look and letterhead, document storage, scheduled jobs and data protection.' },
  { id: 'developer', label: 'Developer', icon: CodeXml,
    description: 'Reference material for the product team.' },
] as const;

export type AdminSectionId = (typeof ADMIN_SECTIONS)[number]['id'];

export interface AdminSetting {
  id: string;
  section: AdminSectionId;
  label: string;
  icon: LucideIcon;
  module: string;
  /** Any one of these grants also shows the setting (SMS + WhatsApp share a screen). */
  alsoModules?: readonly string[];
  /**
   * Shown by role rather than grant: the maker-checker settings to the
   * Executive Director (segregation of duties, deliberately not grantable),
   * the Design System to Admin (a developer reference).
   */
  reviewableBy?: string;
  description: string;
}

export const ADMIN_SETTINGS: readonly AdminSetting[] = [
  { id: 'users', section: 'people', label: 'Users & Roles', icon: Users, module: 'admin:users',
    description: 'Staff accounts, passwords and the roles that decide which modules each person can open.' },
  { id: 'merges', section: 'people', label: 'Patient Merges', icon: GitMerge, module: 'admin:merges',
    description: 'Combine duplicate patient records into one, keeping the old UHID searchable.' },

  { id: 'specialties', section: 'clinical', label: 'Specialties', icon: Tags, module: 'admin:specialties',
    description: 'The specialty list doctors are assigned to, used for same-specialty emergency cover.' },
  // Intake Form and Consent Forms sit together: they configure the two halves
  // of one Registration screen — what it asks for, and what it asks the
  // patient to accept.
  { id: 'intake', section: 'clinical', label: 'Intake Form', icon: ClipboardList, module: 'admin:intake',
    description: 'Which fields Registration collects, in what order, and which of them are mandatory.' },
  { id: 'consent', section: 'clinical', label: 'Consent Forms', icon: PenLine, module: 'admin:consent',
    description: 'The consent forms asked at Registration — their wording, which are mandatory, and when they expire.' },
  // The Vitals stage's Symptoms card (symptom_form_fields). Consultation's
  // "Why today" shows what it recorded.
  { id: 'form-customization', section: 'clinical', label: 'Vitals Form Customization', icon: SlidersHorizontal, module: 'admin:form-customization',
    description: 'The symptom fields asked at Vitals — symptoms list, duration, severity and any others — their input types, options, order, and which are mandatory.' },
  { id: 'clinical-library', section: 'clinical', label: 'Reference Library', icon: Library, module: 'admin:clinical-library',
    alsoModules: ['clinical-library:approve'],
    description: 'The hospital\'s approved clinical references (protocols, pathways, SOPs, formulary notes) that doctors and the Copilot answer from. Each document is approved by a second person.' },
  { id: 'ailments', section: 'clinical', label: 'Ailment Templates', icon: Stethoscope, module: 'admin:ailments',
    reviewableBy: 'Executive Director',
    description: 'Assessment modules used at the Pre-Consult stage. Changes need Executive Director approval.' },
  { id: 'ai-role', section: 'clinical', label: 'AI Assistant Role', icon: BrainCircuit, module: 'admin:ai-role',
    reviewableBy: 'Executive Director',
    description: 'The instructions the AI follows when drafting each document type. Changes need Executive Director approval.' },
  { id: 'emergency', section: 'clinical', label: 'Emergency Department', icon: Ambulance, module: 'admin:emergency',
    description: 'Triage levels and targets, observation and boarding limits, ED bays, MLC jurisdiction rules, payer schemes and paediatric ranges.' },

  { id: 'branches', section: 'facility', label: 'Branches', icon: MapPin, module: 'admin:branches',
    description: 'The sites this hospital runs, their codes and their contact details.' },
  { id: 'wards', section: 'facility', label: 'Wards & Beds', icon: BedDouble, module: 'admin:wards',
    description: 'Inpatient wards, rooms and beds, and the daily charge attached to each class.' },
  { id: 'availability', section: 'facility', label: 'Doctor Availability', icon: CalendarClock, module: 'admin:availability',
    description: 'Consulting hours per doctor, appointment slot length and how far ahead booking opens.' },
  // Per-branch ordering of routine OPD patients (utils/queuePriority.js is
  // what the queue actually sorts by). Facility rather than Clinical: it is a
  // site's front-desk policy, and Emergency/Urgent stay fixed.
  { id: 'queue-priority', section: 'facility', label: 'Queue Prioritization', icon: ListOrdered, module: 'admin:queue-priority',
    description: 'How routine patients are ordered in the OPD queue — appointments, referrals, long-distance travellers, walk-ins and waiting-time limits. Emergency and urgent patients always come first.' },

  // POC-only direct view/edit + Excel bulk upload for drug_master, bypassing
  // the Drug Review queue on purpose — see routes/drug-master.js.
  { id: 'drug-master', section: 'pharmacy-lab', label: 'Drug Master', icon: Database, module: 'admin:drug-master',
    description: 'Direct view, edit and Excel bulk upload of the drug master, bypassing the review queue.' },
  { id: 'drug-review', section: 'pharmacy-lab', label: 'Drug Review', icon: ListChecks, module: 'admin:drug-review',
    description: 'Approval queue for newly captured medicine names before they enter the drug master.' },
  { id: 'formulary', section: 'pharmacy-lab', label: 'Formulary', icon: BookOpen, module: 'admin:formulary',
    description: 'The medicines this hospital stocks and prescribes, and approved substitutions between them.' },
  // Grant stays 'admin:pharmacy-loyalty' (see the header comment).
  { id: 'pharmacy-stores', section: 'pharmacy-lab', label: 'Pharmacy Stores', icon: Warehouse, module: 'admin:pharmacy-stores',
    description: 'The stores at each branch — Main Store, OP and IP pharmacies — and which store each counter sells from.' },
  { id: 'pharmacy-policies', section: 'pharmacy-lab', label: 'Pharmacy Policies', icon: Pill, module: 'admin:pharmacy-loyalty',
    description: 'Loyalty points and members, medicine borrow & return, dispensing & expiry, and substitution approval.' },
  // Its own 'admin:laboratory' grant, not a tab inside the Laboratory module —
  // see CLAUDE.md's Laboratory / LIS section.
  { id: 'laboratory', section: 'pharmacy-lab', label: 'Laboratory Masters', icon: FlaskConical, module: 'admin:laboratory',
    description: 'Test catalogue, panels, sample types, reference ranges and critical-value rules.' },

  { id: 'billing-settings', section: 'billing', label: 'Billing Settings', icon: Receipt, module: 'admin:billing-settings',
    description: 'GST rates, invoice numbering, payment modes, rounding and credit-note rules.' },
  // Grant shared with Billing Settings: both are the hospital's billing configuration.
  { id: 'opd-fees', section: 'billing', label: 'OPD Fee Schedule', icon: ReceiptIndianRupee, module: 'admin:billing-settings',
    description: 'Registration, consultation, procedure and other OPD charges — default fees, fees per branch or department, and discount limits.' },
  { id: 'insurers', section: 'billing', label: 'Insurers & Schemes', icon: ShieldCheck, module: 'admin:payers',
    description: 'Insurers, TPAs and schemes this hospital deals with, and the claim-identity particulars printed on claim documents.' },

  // SMS and WhatsApp share one screen (one MSG91 account), so either channel's
  // grant opens it; SmsGatewayTab shows each channel only to its own grant.
  { id: 'messaging', section: 'communication', label: 'Messaging', icon: MessageSquare, module: 'admin:sms-gateway',
    alsoModules: ['admin:whatsapp-gateway'],
    description: 'MSG91 SMS and WhatsApp — credentials, DLT wordings, Meta-approved templates, automatic notifications and one delivery log.' },

  { id: 'ai-settings', section: 'ai', label: 'AI Settings', icon: Sparkles, module: 'admin:ai-settings',
    description: 'Whether AI features run at all, whether patient consent is required first, and which provider is used.' },
  { id: 'ai-call-log', section: 'ai', label: 'AI Call Log', icon: ScrollText, module: 'admin:ai-call-log',
    description: 'Every AI call, exactly what was sent, and an automatic check that no patient identifiers reached a provider.' },
  { id: 'ai-activity', section: 'ai', label: 'AI Activity', icon: Activity, module: 'admin:ai-activity',
    description: 'Each AI assistant’s health, background AI work that failed, and retry or cancel. Also explains how AI is used here.' },

  { id: 'branding', section: 'system', label: 'Branding', icon: Palette, module: 'admin:branding',
    description: 'Hospital name, logo, colours and the letterhead printed on documents.' },
  { id: 'storage', section: 'system', label: 'Storage', icon: Cloud, module: 'admin:storage',
    description: 'Where uploaded documents and scans are kept, and the credentials for that store.' },
  { id: 'background-jobs', section: 'system', label: 'Background Jobs', icon: Clock, module: 'admin:background-jobs',
    description: 'Scheduled jobs — backups, reminders, summaries — and whether their last run succeeded.' },
  { id: 'data-privacy', section: 'system', label: 'Data Privacy', icon: Lock, module: 'admin:data-privacy',
    description: 'Retention policy, the patient erasure request queue and identity-reveal audit.' },

  { id: 'design-system', section: 'developer', label: 'Design System', icon: LayoutGrid, module: 'admin:design-system',
    reviewableBy: 'Admin',
    description: 'Live gallery of every UI component against this hospital’s own branding. Developer reference.' },
];

/** Settings this user can open — a module grant, a sibling grant, or the role-gated ones. */
export function visibleAdminSettings(modules: readonly string[] | undefined, role: string | undefined): AdminSetting[] {
  return ADMIN_SETTINGS.filter((s) => modules?.includes(s.module)
    || s.alsoModules?.some((m) => modules?.includes(m))
    || (!!s.reviewableBy && role === s.reviewableBy));
}

export function findSection(id: string | undefined) {
  return ADMIN_SECTIONS.find((s) => s.id === id);
}

export function findSetting(id: string | undefined) {
  return ADMIN_SETTINGS.find((s) => s.id === id);
}

export const ADMIN_ROOT = '/admin';
export const sectionPath = (sectionId: string) => `${ADMIN_ROOT}/${sectionId}`;
export const settingPath = (s: Pick<AdminSetting, 'section' | 'id'>) => `${ADMIN_ROOT}/${s.section}/${s.id}`;

/** For the "Find a setting" box and the command palette: label, section and description. */
export function settingMatches(s: AdminSetting, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const sectionLabel = findSection(s.section)?.label ?? '';
  const hay = `${s.label} ${sectionLabel} ${s.description}`.toLowerCase();
  return q.split(/\s+/).every((w) => hay.includes(w));
}

/**
 * Tabs inside a setting, which are part of its URL
 * (/admin/<section>/<setting>/<tab>[/<sub-tab>]) and its breadcrumb. Each
 * setting component reads its active tab through `useAdminSubTab`
 * (adminRoute.tsx) and draws it with `AdminTabs`, so a tab can be linked to,
 * survives a refresh and is walked by Back/Forward. Labels live here, once,
 * so the breadcrumb and the tab strip can't disagree. The first tab is the
 * default a bare setting URL lands on (replaced in history, not pushed).
 */
export interface AdminSubTab {
  id: string;
  label: string;
  /** A second level of tabs inside this one (Messaging → SMS → Connection). */
  children?: readonly AdminSubTab[];
}

const CHANNEL_TABS: readonly AdminSubTab[] = [
  { id: 'connection', label: 'Connection' },
  { id: 'templates', label: 'Templates' },
];

export const ADMIN_SUBTABS: Readonly<Record<string, readonly AdminSubTab[]>> = {
  users: [
    { id: 'users', label: 'Users' },
    { id: 'roles', label: 'Roles' },
  ],
  emergency: [
    { id: 'levels', label: 'Triage levels' },
    { id: 'flow', label: 'Observation & flow' },
    { id: 'bays', label: 'Bays' },
    { id: 'mlc', label: 'MLC rules' },
    { id: 'payers', label: 'Payer schemes' },
    { id: 'pews', label: 'Paediatric ranges' },
  ],
  'pharmacy-policies': [
    { id: 'loyalty', label: 'Loyalty points' },
    { id: 'members', label: 'Members' },
    { id: 'borrow', label: 'Medicine borrow & return' },
    { id: 'dispensing', label: 'Dispensing & expiry' },
  ],
  laboratory: [
    { id: 'tests', label: 'Tests & parameters' },
    { id: 'departments', label: 'Departments' },
    { id: 'categories', label: 'Categories' },
    { id: 'sample-types', label: 'Sample types' },
    { id: 'containers', label: 'Containers' },
    { id: 'packages', label: 'Packages' },
    { id: 'reference-labs', label: 'Reference labs' },
  ],
  insurers: [
    { id: 'payers', label: 'Payers' },
    { id: 'service-master', label: 'Service master' },
    { id: 'branch-profile', label: 'Branch claims profile' },
    { id: 'document-rules', label: 'Document rules' },
    { id: 'tariffs-packages', label: 'Tariffs & packages' },
  ],
  messaging: [
    { id: 'sms', label: 'SMS', children: CHANNEL_TABS },
    { id: 'whatsapp', label: 'WhatsApp', children: CHANNEL_TABS },
    { id: 'notifications', label: 'Notifications' },
    { id: 'log', label: 'Delivery log' },
  ],
  'data-privacy': [
    { id: 'retention', label: 'Retention policy' },
    { id: 'erasure', label: 'Erasure requests' },
  ],
  'clinical-library': [
    { id: 'documents', label: 'Documents' },
    { id: 'settings', label: 'Search settings' },
    { id: 'web-search', label: 'Web search' },
  ],
};

/** The tab list at a depth, given the tab ids above it — or [] past the last level. */
export function subTabsAt(settingId: string, parents: readonly string[]): readonly AdminSubTab[] {
  let level: readonly AdminSubTab[] = ADMIN_SUBTABS[settingId] ?? [];
  for (const p of parents) level = level.find((t) => t.id === p)?.children ?? [];
  return level;
}
